import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Printer, ArrowLeft } from "lucide-react";

const PAPER_FORMATS = {
  "thermal-80": { label: "Térmica 80 mm", width: "80mm", maxWidth: "302px", fontSize: "13px" },
  "thermal-58": { label: "Térmica 58 mm", width: "58mm", maxWidth: "220px", fontSize: "11px" },
  "a4": { label: "Hoja A4 / Carta", width: "auto", maxWidth: "560px", fontSize: "14px" },
};

const STORAGE_KEY = "orden_print_format";

export default function PrintTicket() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const type = params.get("type") || "receipt";
  const [order, setOrder] = useState(null);
  const [format, setFormat] = useState(() => localStorage.getItem(STORAGE_KEY) || "thermal-80");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/orders/${orderId}`); setOrder(data); }
      catch { /* noop */ }
    })();
  }, [orderId]);

  // Persist format and update @page CSS
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, format);
  }, [format]);

  const doPrint = () => {
    window.print();
  };

  if (!order) return <p className="p-8 text-[#7248ae]">Cargando ticket…</p>;

  const fmt = PAPER_FORMATS[format] || PAPER_FORMATS["thermal-80"];

  const itemUnit = (it) => {
    const delta = (it.modifiers || []).reduce((acc, m) => acc + Number(m.price_delta || 0), 0);
    return Number(it.price) + delta;
  };

  const totalPieces = order.items.reduce((acc, it) => acc + Number(it.quantity), 0);
  const r = order.restaurant || {};
  const isKitchen = type === "kitchen";
  const splitMode = order.split_mode || (order.split_count > 1 ? "equal" : "none");
  const splitCount = Math.max(1, Number(order.split_count || 1));
  const splitAmount = splitCount > 1 ? (Number(order.total) / splitCount) : Number(order.total);

  const renderHeader = () => (
    <div className="text-center mb-3">
      <p className="text-xl font-bold uppercase tracking-wider">oRdeN</p>
      <p className="text-[11px]">{r.name || "Restaurante"}</p>
      {r.address && <p className="text-[11px]">{r.address}</p>}
      {r.phone && <p className="text-[11px]">Tel: {r.phone}</p>}
    </div>
  );

  const renderMeta = (labelExtra) => (
    <div className="border-t border-dashed border-black/40 pt-2 mb-2 text-center">
      <p className="font-bold text-sm">{isKitchen ? "*** COMANDA ***" : "*** TICKET ***"}{labelExtra ? ` ${labelExtra}` : ""}</p>
      <p>Mesa: <strong>{order.table_number}</strong></p>
      <p>Mesero: {order.waiter_name}</p>
      <p>{formatDate(order.created_at)}</p>
    </div>
  );

  const renderItemsKitchen = (items) => (
    <>
      {items.map(it => (
        <div key={it.id} className="mb-2">
          <p><strong>{it.quantity}×</strong> {it.name}</p>
          {(it.modifiers || []).length > 0 && (
            <p className="text-[11px]">+ {(it.modifiers || []).map(m => m.name).join(", ")}</p>
          )}
          {it.notes && <p className="text-[11px] italic">→ {it.notes}</p>}
        </div>
      ))}
      <div className="border-t border-dashed border-black/40 pt-2 text-center">
        <p className="font-bold">Total de piezas: {items.reduce((a, it) => a + it.quantity, 0)}</p>
      </div>
    </>
  );

  const renderItemsReceipt = (items, subtotal, splitLabel) => (
    <>
      <table className="w-full">
        <thead>
          <tr className="text-[11px] uppercase">
            <th className="text-left">Cant</th>
            <th className="text-left">Platillo</th>
            <th className="text-right">Importe</th>
          </tr>
        </thead>
        <tbody>
          {items.map(it => (
            <tr key={it.id}>
              <td className="align-top">{it.quantity}</td>
              <td className="align-top">
                {it.name}
                {(it.modifiers || []).length > 0 && (
                  <div className="text-[11px]">+ {(it.modifiers || []).map(m => m.name).join(", ")}</div>
                )}
                {it.notes && <div className="text-[11px] italic">{it.notes}</div>}
              </td>
              <td className="text-right align-top">{formatCurrency(itemUnit(it) * it.quantity)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-dashed border-black/40 mt-2 pt-2 flex justify-between">
        <span className="font-bold">{splitLabel ? "SUBTOTAL" : "TOTAL"}</span>
        <span className="font-bold">{formatCurrency(subtotal)}</span>
      </div>
    </>
  );

  // For receipts in 'items' split mode, render one ticket per person
  const splits = (splitMode === "items" && Array.isArray(order.splits)) ? order.splits : null;

  return (
    <div className="min-h-screen bg-[#fcfbfd] flex flex-col items-center p-6">
      <div className="no-print w-full max-w-2xl mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl border border-[#b182ec]/30 p-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-[#1a0f2e]">{isKitchen ? "Comanda" : "Ticket"}</h1>
            <p className="text-xs text-[#7248ae] leading-snug max-w-md">
              Elige el formato de papel y haz clic en Imprimir. El navegador abrirá el diálogo nativo donde puedes seleccionar la impresora (térmica, láser, PDF, etc.) y guardarla como predeterminada.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:items-end">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-[#7248ae]">Formato</span>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger className="w-44" data-testid="print-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PAPER_FORMATS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => window.history.back()} className="border-[#b182ec]/50" data-testid="back-button">
                <ArrowLeft className="h-4 w-4 mr-1" />Volver
              </Button>
              <Button onClick={doPrint} className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="print-button">
                <Printer className="h-4 w-4 mr-2" />Seleccionar impresora e imprimir
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div
        id="printable"
        className="w-full space-y-6"
        style={{ maxWidth: fmt.maxWidth, fontSize: fmt.fontSize }}
        data-testid="ticket-content"
      >
        {!splits && (
          <div className="bg-white p-5 border border-[#b182ec]/30 rounded-xl text-[13px] leading-tight">
            {renderHeader()}
            {renderMeta()}
            <div className="border-t border-dashed border-black/40 pt-2 mb-2">
              {isKitchen ? renderItemsKitchen(order.items) : renderItemsReceipt(order.items, order.total)}
              {!isKitchen && splitMode === "equal" && (
                <div className="mt-2 p-2 bg-black/5 rounded text-center">
                  <p className="text-[11px] uppercase">Cuenta dividida</p>
                  <p className="font-bold">{formatCurrency(splitAmount)} × {splitCount} personas</p>
                </div>
              )}
              {!isKitchen && order.payment_method && (
                <p className="text-center mt-1 text-[11px]">Pago: {order.payment_method}</p>
              )}
            </div>
            <p className="text-center text-[11px] mt-2">¡Gracias por su preferencia!</p>
          </div>
        )}

        {splits && !isKitchen && splits.map((sp, i) => {
          const items = (sp.item_ids || []).map(iid => order.items.find(x => x.id === iid)).filter(Boolean);
          return (
            <div key={sp.id || i} className="bg-white p-5 border border-[#b182ec]/30 rounded-xl text-[13px] leading-tight page-break">
              {renderHeader()}
              {renderMeta(`— ${sp.label}`)}
              <div className="border-t border-dashed border-black/40 pt-2 mb-2">
                {renderItemsReceipt(items, sp.subtotal, sp.label)}
                <div className="mt-2 p-2 bg-black/5 rounded text-center">
                  <p className="text-[11px] uppercase">Cuenta {i + 1} de {splits.length}</p>
                </div>
                {order.payment_method && (
                  <p className="text-center mt-1 text-[11px]">Pago: {order.payment_method}</p>
                )}
              </div>
              <p className="text-center text-[11px] mt-2">¡Gracias por su preferencia!</p>
            </div>
          );
        })}

        {splits && isKitchen && (
          <div className="bg-white p-5 border border-[#b182ec]/30 rounded-xl text-[13px] leading-tight">
            {renderHeader()}
            {renderMeta()}
            <div className="border-t border-dashed border-black/40 pt-2 mb-2">
              {renderItemsKitchen(order.items)}
            </div>
            <p className="text-center text-[11px] mt-2">¡Gracias por su preferencia!</p>
          </div>
        )}
      </div>

      <style>{`
        @media print {
          @page { size: ${fmt.width === "auto" ? "A4" : fmt.width + " auto"}; margin: ${format === "a4" ? "10mm" : "2mm"}; }
          body { margin: 0; }
          .no-print { display: none !important; }
          .page-break { page-break-after: always; }
          .page-break:last-child { page-break-after: auto; }
        }
      `}</style>
    </div>
  );
}
